import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Users, FileText, Mail, Cpu, Inbox, BellRing, ShieldCheck } from "lucide-react";

export function AdminDashboard() {
  const { api, token } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${api}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data)).catch(() => {});
  }, [api, token]);

  if (!stats) return <p className="text-muted-foreground">Loading…</p>;

  const cards = [
    { icon: Users,       l: "Client accounts",           v: stats.investors },
    { icon: Cpu,         l: "Total Titan Units",         v: (stats.total_units || 0).toLocaleString("fr-FR").replace(/,/g, " ") },
    { icon: FileText,    l: "Documents",                 v: stats.documents },
    { icon: ShieldCheck, l: "Pending verifications",     v: stats.pending_verifications || 0, alert: (stats.pending_verifications || 0) > 0 },
    { icon: BellRing,    l: "Unread notifications",      v: stats.unread_notifications || 0, alert: (stats.unread_notifications || 0) > 0 },
    { icon: Mail,        l: "Contact — new",             v: stats.contact_new },
    { icon: Inbox,       l: "Legacy leads",              v: stats.leads },
  ];

  return (
    <div>
      <p className="overline text-muted-foreground">Admin</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">Overview</h1>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-border border border-border" data-testid="admin-stats-grid">
        {cards.map((c) => (
          <div key={c.l} className={`p-6 ${c.alert ? "bg-amber-500/[0.08]" : "bg-card"}`} data-testid={`admin-stat-${c.l.replace(/\s/g,'-').toLowerCase()}`}>
            <div className="flex items-start justify-between">
              <c.icon className={`w-4 h-4 ${c.alert ? "text-amber-400" : "text-muted-foreground"}`} />
              <p className={`font-display text-3xl md:text-4xl font-light tracking-tight ${c.alert ? "text-amber-400" : ""}`}>{c.v}</p>
            </div>
            <p className="mt-4 overline text-muted-foreground">{c.l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminContact() {
  const { api, token } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${api}/admin/contact`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setItems(r.data.contact_messages)).catch(() => {});
  }, [api, token]);
  return (
    <div>
      <p className="overline text-muted-foreground">Inbound</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">Contact messages</h1>
      <div className="mt-8 border border-border bg-card">
        {items.map((m, i) => (
          <div key={m.id} className={`p-5 ${i > 0 ? "border-t border-border" : ""}`} data-testid={`contact-msg-${m.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-display text-[15px]">{m.subject}</p>
              <p className="font-mono text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
            </div>
            <p className="text-[12px] text-muted-foreground mt-1">{m.name} · {m.email} · {m.company || "—"}</p>
            <p className="mt-3 text-[13px] leading-relaxed">{m.message}</p>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-muted-foreground text-[13px]">No messages.</p>}
      </div>
    </div>
  );
}

export function AdminAudit() {
  const { api, token } = useAuth();
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    axios.get(`${api}/admin/audit`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLogs(r.data.logs)).catch(() => {});
  }, [api, token]);

  return (
    <div>
      <p className="overline text-muted-foreground">System</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tighter">Audit logs</h1>
      <div className="mt-8 border border-border bg-card overflow-x-auto">
        <table className="w-full text-[12px] font-mono" data-testid="admin-audit-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Timestamp", "Actor", "Action", "Target"].map(h => <th key={h} className="p-3 overline text-muted-foreground">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="border-b border-border">
                <td className="p-3 text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</td>
                <td className="p-3">{l.actor_id?.slice(0, 8)}…</td>
                <td className="p-3">{l.action}</td>
                <td className="p-3 text-muted-foreground">{l.target || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
